/**
 * ==========================================================================
 *  DOES THIS LOOK LIKE SOMEONE PLAYING?
 * ==========================================================================
 *  Re-simulating a replay proves the *time* is real. It does not prove a human
 *  produced the inputs: a bot that writes a plausible input stream gets a
 *  genuinely-simulated, genuinely-fast run.
 *
 *  That is a different problem and it has no airtight solution in a browser
 *  game. What it does have is a fingerprint, because a mouse and a keyboard
 *  leave marks that a for-loop does not:
 *
 *   1. MOUSE PIXELS. A browser reports movementX/Y as whole pixels. The game
 *      turns those into radians by multiplying by a known sensitivity, so
 *      every real look delta is an integer multiple of one "pixel" of angle.
 *      A synthesised curve lands between the teeth of that comb.
 *
 *   2. KEY HOLDS. Nobody taps a key for a single 8 ms step. Humans hold keys
 *      for tens of milliseconds at least.
 *
 *  Frame repeats look like a third signal and are reported below, but they are
 *  deliberately NOT scored. Mouse deltas arrive once per rendered frame, so a
 *  60 Hz display leaves every second simulation step untouched - but a 144 Hz
 *  display leaves none of them untouched, and flagging everyone with a good
 *  monitor would be worse than missing a bot. They are kept as context for
 *  whoever reviews a flagged run.
 *
 *  Everything here is forgeable by someone who has read this file - the source
 *  is public, so assume they have. These are a *deterrent and a triage tool*,
 *  not a proof: a high score files the run for review, it does not convict
 *  anyone. The hard guarantee remains "the server computed the time itself".
 * ==========================================================================
 */

import { LOOK, SIMULATION_HZ } from '../config/gameplay';
import { SETTINGS_LIMITS } from '../config/settings';
import { clamp } from '../utils/math';
import { LOOK_QUANTUM, MOVEMENT_MASK, type StepInput } from '../sim/StepInput';

export interface InputAnalysis {
  frames: number;
  /** Fraction of look deltas that sit on the mouse-pixel grid. Real play ~1.0. */
  pixelGridRatio: number;
  /** Fraction of steps whose look is unchanged. Refresh-rate dependent; not scored. */
  heldLookRatio: number;
  /** Longest unbroken stretch of steps that each moved the look. Not scored. */
  longestPerStepLookRun: number;
  /** Shortest movement-key hold, in simulation steps. */
  shortestHoldSteps: number;
  /** How many holds were a single step long. */
  singleStepHolds: number;
  /** Button-state changes per second. */
  inputChangesPerSecond: number;
  /** How many times E was pressed. */
  interactPresses: number;
  /** 0-100. Higher means "less like a person at a keyboard". */
  suspicion: number;
  /** Machine-readable reasons, for the flagged-runs view. */
  flags: string[];
}

/** At or above this a run is stored, but kept off the public board. */
export const SUSPICION_FLAG_THRESHOLD = 55;

/**
 * Runs shorter than this are not analysed for humanness - there simply is not
 * enough signal in two seconds of input, and a false flag is worse than a
 * missed one on a run nobody would care about.
 */
export const MIN_FRAMES_FOR_ANALYSIS = SIMULATION_HZ * 5;

/**
 * How close to a whole pixel a look delta has to land to count as "on grid".
 *
 * Quantising the *absolute* angle each step means a delta can be off by up to
 * one look unit, which at the coarsest legal sensitivity is a bit under a
 * quarter of a pixel. 0.12 leaves room for that without letting arbitrary
 * values through: random deltas land inside it under a quarter of the time.
 */
const PIXEL_GRID_TOLERANCE = 0.12;

/**
 * Below this fraction the look track did not come out of the game's own
 * pixel-to-radians conversion.
 *
 * The game consumes mouse deltas in whole pixels (see LookAccumulator), so an
 * honest run scores essentially 1.0 on every browser and every display. 0.85
 * is a wide margin around that, not a judgement call about how steady someone's
 * hand is.
 */
const PIXEL_GRID_FLOOR = 0.85;
/** How many look changes are needed before the grid ratio means anything. */
const MIN_LOOK_CHANGES = 60;

function emptyAnalysis(frames: number): InputAnalysis {
  return {
    frames,
    pixelGridRatio: 1,
    heldLookRatio: 1,
    longestPerStepLookRun: 0,
    shortestHoldSteps: Number.POSITIVE_INFINITY,
    singleStepHolds: 0,
    inputChangesPerSecond: 0,
    interactPresses: 0,
    suspicion: 0,
    flags: [],
  };
}

export function analyseInputs(frames: readonly StepInput[], mouseSensitivity: number): InputAnalysis {
  if (frames.length === 0) return emptyAnalysis(0);

  // A client-supplied sensitivity outside the settings range is not a signal of
  // anything except that the header was hand-written; clamp and carry on, the
  // grid check below is what actually reacts to it.
  const sensitivity = clamp(
    Number.isFinite(mouseSensitivity) ? mouseSensitivity : 1,
    SETTINGS_LIMITS.mouseSensitivity.min,
    SETTINGS_LIMITS.mouseSensitivity.max,
  );
  /** One mouse pixel, expressed in look-quantisation units. */
  const unitsPerPixel = (LOOK.baseSensitivity * sensitivity) / LOOK_QUANTUM;

  let lookChanges = 0;
  let onGrid = 0;
  let heldLook = 0;
  let perStepRun = 0;
  let longestPerStepRun = 0;
  let buttonChanges = 0;
  let interactPresses = 0;

  const holdStart = new Map<number, number>();
  let shortestHold = Number.POSITIVE_INFINITY;
  let singleStepHolds = 0;

  const closeToInteger = (value: number): boolean =>
    Math.abs(value - Math.round(value)) <= PIXEL_GRID_TOLERANCE;

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const previous = i > 0 ? frames[i - 1] : null;

    if (previous) {
      const dYaw = frame.yawQ - previous.yawQ;
      const dPitch = frame.pitchQ - previous.pitchQ;
      if (dYaw === 0 && dPitch === 0) {
        heldLook++;
        longestPerStepRun = Math.max(longestPerStepRun, perStepRun);
        perStepRun = 0;
      } else {
        perStepRun++;
        lookChanges++;
        // Both axes share one grid; requiring both keeps a bot from hiding a
        // synthesised pitch behind an honest yaw.
        const yawOk = dYaw === 0 || closeToInteger(dYaw / unitsPerPixel);
        const pitchOk = dPitch === 0 || closeToInteger(dPitch / unitsPerPixel);
        if (yawOk && pitchOk) onGrid++;
      }

      if ((frame.buttons & MOVEMENT_MASK) !== (previous.buttons & MOVEMENT_MASK)) buttonChanges++;
    }

    for (let bit = 0; bit < 5; bit++) {
      const mask = 1 << bit;
      const down = (frame.buttons & mask) !== 0;
      const wasDown = previous !== null && (previous.buttons & mask) !== 0;
      if (down && !wasDown) holdStart.set(mask, i);
      if (!down && wasDown) {
        const length = i - (holdStart.get(mask) ?? i);
        if (length > 0) {
          shortestHold = Math.min(shortestHold, length);
          if (length === 1) singleStepHolds++;
        }
        holdStart.delete(mask);
      }
    }

    if ((frame.buttons & (1 << 5)) !== 0) interactPresses++;
  }
  longestPerStepRun = Math.max(longestPerStepRun, perStepRun);

  const comparableSteps = Math.max(1, frames.length - 1);
  const seconds = frames.length / SIMULATION_HZ;

  const analysis: InputAnalysis = {
    frames: frames.length,
    pixelGridRatio: lookChanges === 0 ? 1 : onGrid / lookChanges,
    heldLookRatio: heldLook / comparableSteps,
    longestPerStepLookRun: longestPerStepRun,
    shortestHoldSteps: shortestHold,
    singleStepHolds,
    inputChangesPerSecond: buttonChanges / Math.max(seconds, 1e-6),
    interactPresses,
    suspicion: 0,
    flags: [],
  };

  if (frames.length < MIN_FRAMES_FOR_ANALYSIS) return analysis;

  let suspicion = 0;
  const flags: string[] = [];

  // 1. Off the mouse-pixel grid. The strong signal, and the only one that does
  //    not depend on the player's hardware.
  if (lookChanges >= MIN_LOOK_CHANGES && analysis.pixelGridRatio < PIXEL_GRID_FLOOR) {
    const distance = (PIXEL_GRID_FLOOR - analysis.pixelGridRatio) / PIXEL_GRID_FLOOR;
    suspicion += Math.round(100 * distance);
    flags.push('look-off-pixel-grid');
  }

  // 2. Single-step key taps. One is a fluke; a dozen is a script - 8 ms is
  //    shorter than the debounce on most keyboards, never mind a finger.
  if (analysis.singleStepHolds > 8) {
    suspicion += 25;
    flags.push('single-step-key-taps');
  }
  if (analysis.inputChangesPerSecond > 40) {
    suspicion += 20;
    flags.push('superhuman-input-rate');
  }

  analysis.suspicion = clamp(Math.round(suspicion), 0, 100);
  analysis.flags = flags;
  return analysis;
}
